import PropTypes from 'prop-types';
import './CircularProgress.css';

export const CircularProgress = ({
	title,
	used,
	total,
	size = 120, // SVG size in pixels
	strokeWidth = 8,
	variant = 'auto', // 'auto', 'success', 'warning', 'danger', 'info', 'primary'
	showPercentage = true,
	showRemaining = true,
	showTitle = true,
	showValues = true,
	className = '',
	centerContent, // Custom content for center
	withUpgradePlanIncentive = false,
	onUpgradeClick,
	...props
}) => {
	const percentage = total > 0 ? Math.min(100, (used / total) * 100) : 0;
	const radius = size / 2 - strokeWidth;
	const circumference = 2 * Math.PI * radius;
	const strokeDashoffset = circumference - (percentage / 100) * circumference;

	const isCloseToLimit = percentage >= 90;
	const isAtLimit = percentage >= 100;

	// Auto color based on percentage
	let color = variant;
	if (variant === 'auto') {
		color = isAtLimit ? 'danger' : isCloseToLimit ? 'warning' : 'success';
	}

	// VWA-2818: design-token colors (no Figma frame maps to this dashboard
	// overview page - success/danger are the spec's border-success/
	// border-error tokens; info/primary/secondary have no token equivalent
	// and keep a neutral dark/gray consistent with text-primary/secondary).
	const colorMap = {
		success: '#33C581',
		warning: '#F5A623',
		danger: '#FF1744',
		info: '#007BFF',
		primary: '#161616',
		secondary: '#4D4D4D',
	};

	const strokeColor = colorMap[color] || colorMap.success;

	return (
		<div className={`circular-progress ${className}`} {...props}>
			{showTitle && title && (
				<div className="circular-progress-header">
					<h5 className="circular-progress-title">{title}</h5>
					{showValues && (
						<>
							<div className="circular-progress-main-value">
								<strong>{used}</strong>
							</div>
							<div className="circular-progress-total">
								<h6>
									<strong>Total Limit {total}</strong>
								</h6>
							</div>
						</>
					)}
				</div>
			)}

			<div className="circular-progress-wrapper">
				<svg
					className="circular-progress-svg"
					width={size}
					height={size}
					viewBox={`0 0 ${size} ${size}`}
				>
					{/* Background circle */}
					<circle
						cx={size / 2}
						cy={size / 2}
						r={radius}
						fill="none"
						stroke="#E3E3E3"
						strokeWidth={strokeWidth}
					/>
					{/* Progress circle */}
					<circle
						cx={size / 2}
						cy={size / 2}
						r={radius}
						fill="none"
						stroke={strokeColor}
						strokeWidth={strokeWidth}
						strokeLinecap="round"
						strokeDasharray={circumference}
						strokeDashoffset={strokeDashoffset}
						transform={`rotate(-90 ${size / 2} ${size / 2})`}
						className="circular-progress-bar"
					/>
				</svg>

				{/* Center content */}
				<div className="circular-progress-center">
					{centerContent ? (
						centerContent
					) : (
						<>
							{showPercentage && (
								<>
									<div
										className="circular-progress-percentage"
										style={{ color: strokeColor }}
									>
										{Math.round(percentage)}%
									</div>
									<div className="circular-progress-status">Used</div>
								</>
							)}
						</>
					)}
				</div>
			</div>

			{(showRemaining || withUpgradePlanIncentive) && (
				<div className="circular-progress-footer">
					{/* Remaining indicator */}
					{showRemaining && (
						<div className="tw-text-xs tw-text-text-secondary tw-mt-1 tw-text-center">
							{Math.round(100 - percentage)}% Remaining
						</div>
					)}

					{/* Upgrade button matching Figma green pill with sparkle */}
					{withUpgradePlanIncentive && (
						<button
							type="button"
							onClick={() => {
								if (onUpgradeClick) {
									onUpgradeClick();
								}
							}}
							className="tw-mt-3 tw-flex tw-items-center tw-gap-1.5 tw-rounded-[4px] tw-bg-button-success tw-px-3 tw-py-1.5 tw-text-xs tw-font-semibold tw-text-text-primary hover:tw-opacity-90 tw-transition-opacity"
						>
							<span>Upgrade Plan</span>
							<svg className="tw-h-3 tw-w-3" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
								<path d="M12 0L14.5 9.5L24 12L14.5 14.5L12 24L9.5 14.5L0 12L9.5 9.5L12 0Z" />
							</svg>
						</button>
					)}
				</div>
			)}
		</div>
	);
};

CircularProgress.propTypes = {
	title: PropTypes.string,
	used: PropTypes.number.isRequired,
	total: PropTypes.number.isRequired,
	size: PropTypes.number,
	strokeWidth: PropTypes.number,
	variant: PropTypes.oneOf([
		'auto',
		'success',
		'warning',
		'danger',
		'info',
		'primary',
		'secondary',
	]),
	showPercentage: PropTypes.bool,
	showRemaining: PropTypes.bool,
	showTitle: PropTypes.bool,
	showValues: PropTypes.bool,
	onUpgradeClick: PropTypes.func,
	className: PropTypes.string,
	centerContent: PropTypes.node,
	withUpgradePlanIncentive: PropTypes.bool,
};
